// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { createClient } from "@bitebalance/contracts/client"
import { CONSENT_POLICY_VERSION } from "@/lib/consent"
import { CLIENT_BUILD_HEADER, CLIENT_BUILD_ID } from "@/lib/buildInfo"
import { signalHealthConsentRequired } from "@/lib/healthConsentGate"
import { withWebExtensions, type BiteBalanceClient } from "./webClient"

/**
 * Base URL for the BiteBalance REST API. Defaults to the Vite dev proxy
 * (`/api`, see vite.config.ts) which forwards same-origin to the
 * @bitebalance/contracts mock server (`npm run mock` in packages/contracts,
 * default port 4000) — the mock has no CORS headers, so browser requests
 * to it directly would fail; the proxy keeps everything same-origin in
 * dev. Deployed environments set VITE_API_BASE_URL to the real API
 * Gateway origin at build time.
 */
const DEFAULT_BASE_URL = "/api"

export function createApiClient(getToken: () => string | Promise<string>): BiteBalanceClient {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || DEFAULT_BASE_URL
  // Acceptance 4 (2026-09-04): every call names the bundle that sent it, so
  // a request from a long-lived tab running a superseded build is visible
  // server-side (the build id is otherwise invisible after a deploy).
  const defaultHeaders = {
    "X-Consent-Policy-Version": CONSENT_POLICY_VERSION,
    [CLIENT_BUILD_HEADER]: CLIENT_BUILD_ID,
  }
  // Any endpoint answering 403 HEALTH_AI_CONSENT_REQUIRED raises the consent gate (lib/healthConsentGate.ts) —
  // never a generic error. The ApiError is still thrown to the caller.
  const client = createClient({ baseUrl, getToken, defaultHeaders, onHealthAiConsentRequired: signalHealthConsentRequired })
  // Web-only extensions: `getRecipe(id, { servings })` (recipePortion.ts) and
  // ADR-056 `getConsumptionHistory({ from, to })` (consumptionHistory.ts).
  return withWebExtensions(client, { baseUrl, getToken, defaultHeaders })
}

export type { BiteBalanceClient }
export { ApiError } from "@bitebalance/contracts/client"
